/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ConferenceFormPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Wrapper page component for Conference Form - simplified using API modules.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ConferenceFormView from './ConferenceFormView';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { navigateToConferenceDuplicate } from './conferencesActions';
import { fetchConferenceById, createConference, updateConference } from './api/conferencesApi';

export default function ConferenceFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuthStore();
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [conference, setConference] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const mode = id === 'new' ? 'create' : 'edit';

  // Load conference data for editing or duplicating
  useEffect(() => {
    if (mode === 'edit' && id && id !== 'new') {
      loadConferenceById(id);
    } else if (mode === 'create' && duplicateId) {
      loadConferenceById(duplicateId, true);
    }
  }, [id, mode, duplicateId]);

  const loadConferenceById = async (conferenceId: string, duplicate: boolean = false) => {
    try {
      setLoading(true);
      const loaded = await fetchConferenceById(conferenceId);

      if (duplicate) {
        const copySuffix = t('common:copySuffix', 'Copy');
        setConference({
          ...loaded,
          id: undefined,
          uuid: undefined,
          extension: '',
          name: loaded.name ? `${loaded.name} ${copySuffix}`.trim() : loaded.name,
          description: loaded.description ? `${loaded.description} ${copySuffix}`.trim() : loaded.description,
          created_at: undefined,
          updated_at: undefined,
        });
      } else {
        setConference(loaded);
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error loading conference:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSave = async (conferenceData: any) => {
    let savedData;
    
    if (mode === 'edit' && id) {
      savedData = await updateConference(id, conferenceData);
      // Note: We don't reload here to avoid interfering with navigation actions
      // The form will reload when the component re-mounts if needed
    } else {
      savedData = await createConference(conferenceData);
      // Note: Navigation after create is now handled by the dropdown actions
      // in ConferenceFormView, so we don't navigate here automatically
    }

    return savedData;
  };

  const handleBack = () => {
    navigate('/pbx/applications/conferences');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDuplicate = (conf: any) => {
    if (!(conf?.uuid || conf?.id)) return;
    navigateToConferenceDuplicate(navigate, conf.uuid || conf.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ConferenceFormView
      onBack={handleBack}
      onSave={handleSave}
      conference={conference}
      mode={mode}
      onDuplicate={handleDuplicate}
    />
  );
}
